import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Layer } from 'grommet';
import { Text } from 'grommet';
import { observer } from 'mobx-react-lite';
import { ApiError } from '/src/api';
import { CreateBankAccountRequest, CreateRecipientRequest } from '/src/api/services/StripeGlobalPayoutsService';
import { StripeGlobalPayoutsService } from '/src/api/services/StripeGlobalPayoutsService';
import { Box, Card, CardBody, CardHeader, InfoBox, Input, Line, Select } from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { Client } from '/src/lib/models';
import { CountryId } from '/src/lib/types';

export type AddRecipientModalProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  onRecipientAdded: () => void;
  clientId?: number;
  clientName?: string;
  client?: Client;
};

export const AddRecipientModal: React.FC<AddRecipientModalProps> = observer(
  ({ isVisible, setIsVisible, onRecipientAdded, clientId, clientName, client }) => {
    const isCanada = client?.hq_address_region?.country_id === CountryId.Canada;
    const userStore = useUserStore();
    const globalStore = useGlobalStore();

    const regionOptions = globalStore.regions
      .filter((region) => region.country_id === CountryId.USA && region.short_code)
      .map((region) => ({
        label: `${region.name} (${region.short_code})`,
        value: region.id,
      }));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submissionInProgress, setSubmissionInProgress] = useState(false);
    const [bankData, setBankData] = useState<CreateBankAccountRequest>({
      routing_number: '',
      account_number: '',
      bank_account_type: 'checking',
    });
    const [onboardingEmail, setOnboardingEmail] = useState('');
    const [isSendingOnboarding, setIsSendingOnboarding] = useState(false);

    const [formData, setFormData] = useState<CreateRecipientRequest>({
      contact_email: '',
      registered_name: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        region_id: 0,
        postal_code: '',
      },
    });

    useEffect(() => {
      if (!isVisible || !clientName) return;
      setFormData((prev) => {
        if (prev.registered_name.trim().length > 0) return prev;
        return { ...prev, registered_name: clientName };
      });
    }, [isVisible, clientName]);

    useEffect(() => {
      if (!isVisible || !isCanada || !client) return;
      setFormData((prev) => ({
        ...prev,
        address: {
          line1: client.hq_address_line1 ?? '',
          line2: client.hq_address_line2 ?? '',
          city: client.hq_address_city ?? '',
          region_id: client.hq_address_region_id ?? 0,
          postal_code: client.hq_address_post_code ?? '',
        },
      }));
    }, [isVisible, isCanada, client]);

    const updateAddressField = (field: 'line1' | 'line2' | 'city' | 'postal_code', value: string) => {
      setFormData((prev) => {
        const prevAddress = prev.address ?? {
          line1: '',
          line2: '',
          city: '',
          region_id: 0,
          postal_code: '',
        };
        return { ...prev, address: { ...prevAddress, [field]: value } };
      });
    };

    const updateRegion = (regionId: number) => {
      setFormData((prev) => {
        const prevAddress = prev.address ?? {
          line1: '',
          line2: '',
          city: '',
          region_id: 0,
          postal_code: '',
        };
        return { ...prev, address: { ...prevAddress, region_id: regionId } };
      });
    };

    const validateForm = () => {
      const newErrors: Record<string, string> = {};
      if (!formData.registered_name.trim()) newErrors.registered_name = 'Registered Name is required';
      if (!formData.contact_email.trim()) newErrors.contact_email = 'Email is required';
      const hasRouting = bankData.routing_number.trim().length > 0;
      const hasAccount = bankData.account_number.trim().length > 0;
      if (hasRouting && !hasAccount) newErrors.account_number = 'Account Number is required';
      if (hasAccount && !hasRouting) newErrors.routing_number = 'Routing Number is required';

      if (isCanada) {
        const address = formData.address;
        if (!address?.line1?.trim()) newErrors.line1 = 'Address is required';
        if (!address?.city?.trim()) newErrors.city = 'City is required';
        if (!address?.region_id) newErrors.region_id = 'State is required';
        if (!address?.postal_code?.trim()) newErrors.postal_code = 'Postal Code is required';
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const initialBankData: CreateBankAccountRequest = {
      routing_number: '',
      account_number: '',
      bank_account_type: 'checking',
    };

    const initialFormData: CreateRecipientRequest = {
      contact_email: '',
      registered_name: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        region_id: 0,
        postal_code: '',
      },
    };

    const resetForm = () => {
      setBankData(initialBankData);
      setFormData(initialFormData);
      setErrors({});
    };

    const handleClose = () => {
      resetForm();
      setOnboardingEmail('');
      setIsVisible(false);
    };

    const handleSubmit = async () => {
      if (!validateForm()) return;
      if (submissionInProgress) return;
      if (!clientId) {
        toast.error('Client ID is required');
        return;
      }

      setSubmissionInProgress(true);
      setIsSubmitting(true);

      try {
        const hasBankData = bankData.routing_number.trim().length > 0 && bankData.account_number.trim().length > 0;

        const recipientData: CreateRecipientRequest = {
          contact_email: formData.contact_email,
          registered_name: formData.registered_name,
          address: isCanada ? formData.address : undefined,
        };

        const result = await StripeGlobalPayoutsService.createRecipient(
          clientId,
          recipientData,
          hasBankData ? bankData : undefined
        );

        if (result.stripe_global_payouts_account_id) {
          toast.success(hasBankData ? 'Recipient and bank account added successfully' : 'Recipient added successfully');
          resetForm();
          setIsVisible(false);
          onRecipientAdded();
        } else {
          toast.error('Failed to add recipient');
        }
      } catch (error: unknown) {
        console.error('Error adding recipient:', error);

        let errorMsg = 'Failed to add recipient';
        if (error instanceof ApiError && error.body) {
          const body = error.body as { error?: string; message?: string };
          errorMsg = body.error || body.message || errorMsg;
        } else if (error instanceof Error) {
          errorMsg = error.message;
        }

        toast.error(errorMsg);
      } finally {
        setIsSubmitting(false);
        setSubmissionInProgress(false);
      }
    };

    const handleSendOnboardingEmail = async () => {
      if (!onboardingEmail.trim()) {
        setErrors((prev) => ({ ...prev, onboarding_email: 'Email is required' }));
        return;
      }
      if (!clientId) {
        toast.error('Client ID is required');
        return;
      }

      setIsSendingOnboarding(true);
      try {
        const result = await StripeGlobalPayoutsService.createOnboardingLink(clientId, onboardingEmail.trim());
        if (result.url) {
          window.open(result.url, '_blank');
          toast.success('Onboarding link opened');
          setOnboardingEmail('');
          setIsVisible(false);
          onRecipientAdded();
        } else {
          toast.error('Failed to create onboarding link');
        }
      } catch (error: unknown) {
        console.error('Error creating onboarding link:', error);
        let errorMsg = 'Failed to create onboarding link';
        if (error instanceof ApiError && error.body) {
          const body = error.body as { error?: string; message?: string };
          errorMsg = body.error || body.message || errorMsg;
        } else if (error instanceof Error) {
          errorMsg = error.message;
        }
        toast.error(errorMsg);
      } finally {
        setIsSendingOnboarding(false);
      }
    };

    if (!isVisible) return null;

    return (
      <Layer background="transparent">
        <Card width="100%" style={{ maxWidth: userStore.isFuseAdmin ? 760 : 600, minWidth: 500 }}>
          <CardHeader title="Create Stripe Recipient" />
          <CardBody pad={{ vertical: 'medium', horizontal: 'large' }}>
            <Box gap="medium">
              {userStore.isFuseAdmin ? (
                <Box>
                  <Box direction="row">
                    <Box flex>
                      <Input
                        label="Registered Name"
                        value={formData.registered_name}
                        setValue={(value) => setFormData({ ...formData, registered_name: value })}
                        placeholder="Enter registered name"
                        required
                        error={errors.registered_name}
                        style={{ width: '100%' }}
                        margin={{ bottom: 'small' }}
                      />
                      <Input
                        label="Email Address"
                        value={formData.contact_email}
                        setValue={(value) => setFormData({ ...formData, contact_email: value })}
                        placeholder="Enter email address"
                        type="email"
                        required
                        error={errors.contact_email}
                        style={{ width: '100%' }}
                        margin={{ bottom: 'small' }}
                      />
                      {isCanada && (
                        <>
                          <Input
                            label="Address"
                            value={formData.address?.line1 ?? ''}
                            setValue={(value) => updateAddressField('line1', value)}
                            placeholder="Enter street address"
                            required
                            error={errors.line1}
                            style={{ width: '100%' }}
                            margin={{ bottom: 'small' }}
                          />
                          <Input
                            label="Address Line 2"
                            value={formData.address?.line2 ?? ''}
                            setValue={(value) => updateAddressField('line2', value)}
                            placeholder="Apartment, suite, unit, etc. (optional)"
                            style={{ width: '100%' }}
                            margin={{ bottom: 'small' }}
                          />
                          <Box direction="row" gap="small">
                            <Box flex>
                              <Input
                                label="City"
                                value={formData.address?.city ?? ''}
                                setValue={(value) => updateAddressField('city', value)}
                                placeholder="Enter city"
                                required
                                error={errors.city}
                                style={{ width: '100%' }}
                                margin={{ bottom: 'small' }}
                              />
                            </Box>
                            <Box flex>
                              <Input
                                label="Postal Code"
                                value={formData.address?.postal_code ?? ''}
                                setValue={(value) => updateAddressField('postal_code', value)}
                                placeholder="Enter postal code"
                                required
                                error={errors.postal_code}
                                style={{ width: '100%' }}
                                margin={{ bottom: 'small' }}
                              />
                            </Box>
                          </Box>
                          <Select
                            label="State"
                            value={formData.address?.region_id || ''}
                            setValue={(value) => updateRegion(Number(value))}
                            options={regionOptions}
                            placeholder="Select state"
                            required
                            error={errors.region_id}
                            margin={{ bottom: 'small' }}
                          />
                        </>
                      )}
                    </Box>
                    <Line direction="vertical" margin="2rem" />
                    <Box flex>
                      <Input
                        label="Routing Number"
                        value={bankData.routing_number}
                        setValue={(value) => setBankData({ ...bankData, routing_number: value })}
                        placeholder="Enter routing number"
                        error={errors.routing_number}
                        style={{ width: '100%' }}
                        margin={{ bottom: 'small' }}
                      />
                      <Input
                        label="Account Number"
                        value={bankData.account_number}
                        setValue={(value) => setBankData({ ...bankData, account_number: value })}
                        placeholder="Enter account number"
                        error={errors.account_number}
                        style={{ width: '100%' }}
                        margin={{ bottom: 'small' }}
                      />
                    </Box>
                  </Box>
                  <Box direction="row" gap="small" justify="end" margin={{ top: 'small' }}>
                    <Button
                      label="Cancel"
                      onClick={handleClose}
                      disabled={isSubmitting}
                      style={{ fontWeight: 'normal' }}
                    />
                    <Button
                      primary
                      label={isSubmitting ? 'Loading...' : 'Create Recipient'}
                      onClick={handleSubmit}
                      disabled={isSubmitting || submissionInProgress}
                      style={{ fontWeight: 'normal' }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Text size="large" color="#5A6672" margin={{ bottom: 'small', top: 'none' }} weight={300}>
                    Stripe-Hosted Onboarding
                  </Text>
                  <InfoBox margin={{ bottom: 'small' }}>
                    <Text size="small">
                      Open a Stripe-hosted form where the recipient can provide their own payment details.
                    </Text>
                  </InfoBox>
                  <Input
                    label="Email Address"
                    value={onboardingEmail}
                    setValue={(value) => {
                      setOnboardingEmail(value);
                      if (errors.onboarding_email) {
                        setErrors((prev) => ({ ...prev, onboarding_email: '' }));
                      }
                    }}
                    placeholder="Enter email address"
                    type="email"
                    required
                    error={errors.onboarding_email}
                    style={{ width: '100%' }}
                    margin={{ bottom: 'small' }}
                  />
                  <Box direction="row" gap="small" justify="end" margin={{ top: 'small' }}>
                    <Button label="Cancel" onClick={handleClose} style={{ fontWeight: 'normal' }} />
                    <Button
                      primary
                      label={isSendingOnboarding ? 'Loading...' : 'Open Onboarding Form'}
                      onClick={handleSendOnboardingEmail}
                      disabled={isSendingOnboarding}
                      style={{ fontWeight: 'normal' }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </CardBody>
        </Card>
      </Layer>
    );
  }
);
